import { useLayoutEffect, useRef, useState, type RefObject } from "react";
import { useResizablePanelWidth } from "@/hooks/useResizablePanelWidth";

/**
 * Width policy for the building-data attribute panel: the persisted preference,
 * the responsive default, and the clamp that keeps the field list on screen.
 * Kept out of `BuildingDataInspector` so that file stays the panel's markup.
 */

const INSPECTOR_WIDTH_STORAGE_KEY = "building-data-inspector:v1";

/**
 * Narrower than the hook's 360px default: the panel's own layout (label column
 * + value control) still reads at 320, and a user who wants the field list back
 * should be able to shrink to that rather than close the panel outright.
 */
const MIN_INSPECTOR_WIDTH = 320;

/** Room the field list keeps whatever the panel does. */
const MIN_FIELD_LIST_WIDTH = 320;

/**
 * The pre-resize widths, kept as the default so nothing moves. It stays live:
 * the hook only persists a width the user actually dragged, so until then this
 * is re-read on every viewport change and the panel keeps its old responsive
 * step at 1500px.
 */
function defaultInspectorWidth(): number {
  if (typeof window === "undefined") return 340;
  return window.innerWidth >= 1500 ? 392 : 340;
}

/**
 * The widest the panel may be without squeezing the field list out of the row.
 *
 * `useResizablePanelWidth`'s own ceiling is 65 % of the VIEWPORT, which is right
 * for its other callers because they are fixed overlays. This one is an in-flow
 * `shrink-0` flex child sharing an `overflow-hidden` row with the section nav
 * and a `min-w-0` `<main>`: on a 1280px screen with the app sidebar open, 65 %
 * of the viewport is more than the row has left once the nav is paid for, so
 * `<main>` collapses to nothing and no scroll position brings the field list
 * back.
 *
 * Measured rather than derived from the nav's width, because the nav collapses
 * to a rail and disappears entirely below its own breakpoint. `undefined` while
 * the row has no layout at all (jsdom), which leaves the viewport ceiling in
 * charge.
 *
 * Measured BEFORE paint: the panel unmounts when it closes, so every open
 * starts with no clearance, and an effect running after paint would show one
 * frame at the unclamped stored width — a 900px preference on a 1280px screen
 * squeezes `<main>` to nothing and then snaps back. The panel is in flow, so
 * that is a visible jump of the whole row.
 */
function useFieldListClearance(panel: RefObject<HTMLElement>): number | undefined {
  const [clearance, setClearance] = useState<number>();

  useLayoutEffect(() => {
    const row = panel.current?.parentElement;
    if (!row) return;
    // Re-queried per measurement: collapsing the nav re-renders it, and the
    // width we owe it is whatever it is rendering now (212px, a 40px rail, or
    // nothing at all below 1280px).
    const measure = () => {
      if (!row.clientWidth) return;
      const nav = row.querySelector<HTMLElement>(":scope > nav");
      setClearance(row.clientWidth - (nav?.offsetWidth ?? 0) - MIN_FIELD_LIST_WIDTH);
    };
    measure();
    // Without the observer the first measurement still stands, and the clamp is
    // optional anyway — the viewport ceiling stays in charge from there.
    if (typeof ResizeObserver === "undefined") return;
    // The row covers a window or app-sidebar resize; `<main>` covers the nav
    // collapsing, which leaves the row's own width untouched. Neither reading
    // depends on the panel's width, so the panel resizing `<main>` re-measures
    // to the same number and settles rather than looping.
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    const fieldPane = row.querySelector<HTMLElement>(":scope > main");
    if (fieldPane) observer.observe(fieldPane);
    return () => observer.disconnect();
  }, [panel]);

  return clearance;
}

/**
 * The attribute panel's width: clamped for rendering to [320px, whichever is
 * smaller of 65 % of the viewport and the room the row has left], with a
 * dragged width persisted in localStorage. `panelRef` goes on the panel
 * element, whose parent is the row being measured.
 */
export function useInspectorPanelWidth() {
  const panelRef = useRef<HTMLElement>(null);
  const clearance = useFieldListClearance(panelRef);
  const resize = useResizablePanelWidth(INSPECTOR_WIDTH_STORAGE_KEY, defaultInspectorWidth(), {
    minWidth: MIN_INSPECTOR_WIDTH,
    maxWidth: clearance,
  });
  return { panelRef, ...resize };
}
